/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Image,
	Menu,
	ActionIcon,
	rem,
} from '@mantine/core';

// Icons
import { IconEdit, IconTrash } from '@tabler/icons-react';
import IconDotsVertical from '@assets/icons/dots-vertical.svg';
import IconExternalLink from '@assets/icons/external-link.svg';

// Styles
import classes from './CellUsersActions.module.css';
import styles from './CellUsersActions.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { TParsedSite } from '@features/resources/sites/types';
import { ACLS } from '@config/constants/acls';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type CellConversionFactorsActionsProps = {
	onViewMore?: (data: TParsedSite) => void;
	onDelete?: (data: TParsedSite) => void;
	onEdit?: (data: TParsedSite) => void;
	row?: TParsedSite | null;
	opened?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const CellUsersActions = (
	props: CellConversionFactorsActionsProps
) => {
	/* ******************************** I18n ******************************* */
	const { t } = useTranslation();

	/* ******************************** HELPERS ******************************* */
	const handleviewmore = (
		event: React.MouseEvent<HTMLElement>
	) => {
		event.stopPropagation();
		if (props && props.row) {
			props.onViewMore!(props.row);
		}
	};

	const handledeleteitem = (
		event: React.MouseEvent<HTMLElement>
	) => {
		event.stopPropagation();
		if (props && props.row) {
			props.onDelete!(props.row);
		}
	};

	const handleedititem = (
		event: React.MouseEvent<HTMLElement>
	) => {
		event.stopPropagation();
		if (props && props.row) {
			props.onEdit!(props.row);
		}
	};

	const { checkAcl, checkAccess } = useAuthorization();
	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Menu
				opened={props.opened}
				shadow="xs"
				classNames={styles}
			>
				<Menu.Target>
					<ActionIcon
						variant="transparent"
						data-testid="W2FtGXR5_Z5jdHi6B-menu"
						className={classes.customSize}
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						<Image
							w={rem(25)}
							h={rem(25)}
							fit="contain"
							src={IconDotsVertical}
						/>
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item
						data-testid="W2FtGXR7_Z5jdHi6B-menu"
						onClick={handleviewmore}
						color="var(--mantine-color-black)"
						className={styles.item}
						leftSection={
							<Image
								w={rem(20)}
								h={rem(20)}
								fit="contain"
								src={IconExternalLink}
							/>
						}
					>
						{t('global.viewMore')}
					</Menu.Item>
					<Menu.Divider />

					{checkAcl(ACLS.USERS.UPDATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<>
								<Menu.Item
									data-testid="W2FtGXR7_Z5jdHi6Z-menu"
									onClick={handleedititem}
									color="var(--mantine-color-black)"
									className={styles.item}
									leftSection={
										<IconEdit
											style={{
												width: rem(20),
												height: rem(20),
											}}
										/>
									}
								>
									{t('global.edit')}
								</Menu.Item>
								<Menu.Divider />
							</>
						)}

					{checkAcl(ACLS.USERS.DELETE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<Menu.Item
								disabled={
									props.row?.meterPointsCount !==
									undefined
										? Number(
												props.row?.meterPointsCount
											) > 0
											? true
											: false
										: false
								}
								data-testid="W2FtGXR6_Z5jdHi6B-menu"
								onClick={handledeleteitem}
								className={styles.item}
								color="red"
								leftSection={
									<IconTrash
										style={{
											width: rem(20),
											height: rem(20),
										}}
									/>
								}
							>
								{t('global.delete')}
							</Menu.Item>
						)}
				</Menu.Dropdown>
			</Menu>
		</Box>
	);
};
